import { Box, Button, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { IWorkflow } from '@modules/admin/Tools/ChangeWorkflowStep/interfaces';
import { requestGetWorkflowStepsByWorkflowId } from '@modules/admin/Tools/ChangeWorkflowStep/requests';
import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { IOption } from '@shared/interfaces';
import { MenuWorkflowChip } from '@styles/Admin-tools/QueueConfiguration/MenuWorkflow';

import { IMenuWorkflowProps, IWorflowStepType, IWorkflowDetails, IWorkflowStepDetails } from '../types';

type VariantsType = { all: string; choose: string };

const Variants: VariantsType = {
  all: 'All steps',
  choose: 'Choose step',
};

const stepVariantOptions: IOption[] = [
  {
    value: Variants.all,
    label: Variants.all,
  },
  {
    value: Variants.choose,
    label: Variants.choose,
  },
];

const stepTypeAsStep = 'Step';

const getChipLabel = (data: IWorkflowDetails | IWorkflowStepDetails, type: string) => {
  if (type === IWorflowStepType.Workflow) {
    return `${type}[${data.numericId}] - ${data.name}`;
  }

  return `${type}[${data.numericId}] - ${data.workflowName} (${data.entityType}) - ${data.name}`;
};

const getUpdatedData = (menuWorkflows: IWorkflowDetails[]) => {
  const updatedData = menuWorkflows.map((workflow) => (
    {
      ...workflow,
      type: IWorflowStepType.Workflow,
      workflowSteps: workflow.workflowSteps?.map((step) => (
        {
          ...step,
          workflowName: workflow.name,
          entityType: workflow.entityType,
          type: IWorflowStepType.Step,
        }
      ))
    }
  ));

  return updatedData;
};

export const MenuWorkflow = ({ queueSubGroup, menuWorkflows, setFieldValue, handleSubmit }: IMenuWorkflowProps) => {
  const [workflow, setWorkflow] = useState('');
  const [steps, setSteps] = useState<string[]>([]);
  const [stepsVariant, setStepVariant] = useState(Variants.all);
  const [stepOptions, setStepOptions] = useState<IOption<unknown>[]>([]);
  const [mainList, setMainList] = useState<IWorkflowDetails[]>([]);
  const [flag, setFlag] = useState(false);


  const { data: fetchedWorkflows = [] } = useSWR<IWorkflow[]>(`${SERVICES.WORKFLOW}/workflows/all`);

  const workflowOptions = useMemo(() => {
    const options = fetchedWorkflows.map((workflow) => ({
      value: workflow.id,
      label: `[${workflow.numericId}] - ${workflow.name} - ${workflow.entityType}`
    }));

    return options;
  }, [fetchedWorkflows]);

  const workflowsWithoutSteps = useMemo(() => (
    mainList.filter((item) => item.workflowSteps?.length === 0).map((val) => val.id)
  ), [mainList]);

  const existingSteps = useMemo(() => (
    mainList.find((item) => item.id === workflow)?.workflowSteps?.map((val) => val.id)
  ), [mainList, workflow]);

  useEffect(() => {
    setWorkflow('');
    setSteps([]);
  }, [queueSubGroup]);

  useEffect(() => {
    if (menuWorkflows) {
      setMainList(getUpdatedData(menuWorkflows));
    }
  }, [menuWorkflows]);

  useEffect(() => {
    flag && handleSubmit?.();
    setFlag(false);
  }, [flag]);

  useEffect(() => {
    const getWorkflowSteps = async () => {
      if (workflow) {
        const workflowStepResponse = await requestGetWorkflowStepsByWorkflowId(workflow);
        if (stepsVariant === Variants.choose) {
          const formattedSteps = (workflowStepResponse || []).filter((step) => step.stepType === stepTypeAsStep);
          const options = formattedSteps.map((step) => ({
            value: step.id,
            label: `[${step.numericId}] - ${step.name}`
          }));
          setStepOptions(options);
        }
        setSteps([]);
      }
    };

    getWorkflowSteps();
  }, [stepsVariant, workflow]);

  const handleDelete = useCallback((selectedChip: IWorkflowDetails) => () => {
    let list = mainList.map((item) => ({
      id: item.id,
      workflowSteps: item.workflowSteps?.map((step) => ({ id: step.id })) ?? []
    }));

    if (selectedChip.type === IWorflowStepType.Workflow) {
      list = list.filter((item) => item.id !== selectedChip.id);
    } else {
      list = list.map((item) => (
        item.id === selectedChip.workflowId ?
          {
            ...item,
            workflowSteps: item.workflowSteps.filter((step) => step.id !== selectedChip.id)
          }
          : { ...item }
      ));
    }

    setFieldValue?.('menuWorkflows', list);
    setFlag(true);
  }, [steps, mainList]);

  const updateWorkflow = useCallback((workflow: string) => {
    if (workflow) {
      setWorkflow(workflow);
      setStepVariant(Variants.all);
    }
  }, []);

  const addMenuWorkflows = useCallback(() => {
    const data = {
      id: workflow,
      workflowSteps: steps.map((step) => ({ id: step })) ?? []
    };
    const existing = mainList.find((item) => item.id === data.id);
    const list = mainList.map((item) => ({
      id: item.id,
      workflowSteps: item.workflowSteps?.map((step) => ({ id: step.id })) ?? []
    }));

    if (!existing) {
      list.push(data);
    } else if (stepsVariant === Variants.all) {
      const index = list.findIndex((item) => item.id === existing.id);
      list[index].workflowSteps = [];
    } else {
      const index = list.findIndex((item) => item.id === existing.id);
      list[index].workflowSteps = [...list[index].workflowSteps, ...data.workflowSteps];
    }

    setFieldValue?.('menuWorkflows', list);
    setFlag(true);
    setWorkflow('');
  }, [workflow, steps, mainList, stepsVariant]);

  return (
    <Box mt={4}>
      <Typography variant="h3">Menu subgroup workflows</Typography>

      <Stack marginY={2} flexDirection="column">
        {mainList?.map((data) => {
          return (
            !data.workflowSteps || data.workflowSteps?.length === 0 ?
              <MenuWorkflowChip
                key={data.id}
                type={IWorflowStepType.Workflow}
                label={getChipLabel(data, IWorflowStepType.Workflow)}
                onDelete={handleDelete(data)}
              />
              :
              data.workflowSteps.map((item) =>
                <MenuWorkflowChip
                  key={item.id}
                  type={IWorflowStepType.Step}
                  label={getChipLabel(item, IWorflowStepType.Step)}
                  onDelete={handleDelete(item)}
                />
              )
          );
        })}
      </Stack>

      <Stack direction="row" alignItems="center" mb={2}>
        <Box width="60%">
          <SelectAutocomplete
            label="Add Workflow to Menu"
            options={workflowOptions}
            value={workflow}
            getOptionDisabled={(option: IOption) => workflowsWithoutSteps.includes(option?.value as string) ?? false}
            onChange={updateWorkflow}
          />
        </Box>
        {workflow && (
          <Box
            sx={{
              width: '40%',
              marginLeft: '10px',
            }}
          >
            <SelectAutocomplete
              label={'[Optional] Add Workflow Step to Menu'}
              value={stepsVariant}
              options={stepVariantOptions}
              onChange={setStepVariant}
            />
          </Box>
        )}
      </Stack>

      {stepsVariant === Variants.choose && workflow && (
        <Box width="100%" mb={2}>
          <SelectAutocomplete
            multiple
            options={stepOptions}
            value={steps}
            getOptionDisabled={(option) => existingSteps?.includes(option.value as string) ?? false}
            onChange={(steps: string[]) => setSteps(steps)}
          />
        </Box>
      )}

      <Button
        variant="contained"
        disabled={stepsVariant === Variants.all ? !workflow : (!workflow || !steps.length)}
        onClick={addMenuWorkflows}
      >
        Add
      </Button>

    </Box>
  );
};
